import type { Entry } from '../types/data';
import { useState } from 'react';
import { ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';
import Modal from './Modal';
import { Document, Page } from 'react-pdf';
import '../utils/pdfConfig';

const apiURL= 'https://api.epsonrewards.my/cms/update-status.php?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJhcGlfa2V5IjoiZXBzb25fYXBpX2FjY2VzcyJ9.8KZZcKpJmqt_hH2_tGFJP9HM8Vp-YSUqWF_kYYpVNx4';

interface EditModalProps {
    isOpen: boolean;
    onClose: () => void;
    userData: Entry;
    toast: {show: boolean, message: string};
    setToast: Function;
    toastId: number;
    setToastId: Function;
    fetchAllData: Function;
}

export default function EditModal({ isOpen, onClose, userData, toast, setToast, toastId, setToastId, fetchAllData }: EditModalProps) {
  const [selectedPdf, setSelectedPdf] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [pdfNumPages, setPdfNumPages] = useState<number | null>(null);
  const [pdfCurrentPage, setPdfCurrentPage] = useState<number>(1);
  const [pdfLoadError, setPdfLoadError] = useState<boolean>(false);
  
  function viewPDF(url: string) {
    setSelectedPdf(url);
    setPdfCurrentPage(1);
    setPdfLoadError(false);
  }
  
  function onDocumentLoadSuccess({ numPages }: { numPages: number }) {
    setPdfNumPages(numPages);
    setPdfLoadError(false);
  }
  
  function onDocumentLoadError(error: Error) {
    console.error('Error loading PDF:', error);
    setPdfLoadError(true);
  }
    const [newStatus, setNewStatus] = useState(userData.status);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [reason, setReason] = useState(userData.reject_reason ?? '');
    
    // State for collapsible sections
    const [sectionsState, setSectionsState] = useState({
        userInfo: true,
        productInfo: false,
        purchaseInfo: false
    });
    
    const toggleSection = (section: 'userInfo' | 'productInfo' | 'purchaseInfo') => {
        setSectionsState(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    const handleSave = async () => {
        // check if reason is given 
        if (newStatus == 'rejected' || newStatus == 'reject permanently') {
            if (reason.trim() === '') {
                alert('Please enter a reason');
                return;
            }
        }

        const formData = new FormData();
        formData.append("id", String(userData.id));
        formData.append("status", newStatus);
        formData.append("reason", reason);

        const response = await fetch(apiURL, {
            method: 'POST',
            body: formData
        });

        const data = await response.json();
        if (data.code === 200) {
            clearTimeout(toastId);
            setToast({show: true, message: 'Status updated successfully.'});
            setToastId(setTimeout(()=>setToast({show: false, message: ''}), 3000));
            await fetchAllData();
            onClose();
        }
    };

    

    if (!isOpen) return null;

    return (
        <>
        <div className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm bg-black bg-opacity-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-3xl shadow-xl transition-colors duration-200">
                <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-6">Edit Data</h2>
                
                {/* Status section - Prominently displayed at the top */}
                <div className="mb-6 p-5 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <div className="mb-3">
                        <p className="font-semibold text-gray-700 dark:text-gray-300">
                            Current Status: 
                            <span className={`ml-1 font-normal ${
                                userData.status === 'pending' ? 'text-yellow-600 dark:text-yellow-400' : 
                                userData.status === 'rejected' ? 'text-red-600 dark:text-red-400' : 
                                userData.status === 'reject permanently' ? 'text-red-800 dark:text-red-500' : 
                                userData.status === 'redeemed' ? 'text-green-800 dark:text-green-400' : 
                                'text-gray-800 dark:text-gray-200'
                            }`}>
                                {userData.status}
                            </span>
                        </p>
                    </div>
                    
                    <div>
                        <p className="font-semibold text-gray-700 dark:text-gray-300 mb-2">Update Status To:</p>
                        <div 
                            onClick={() => setIsDropdownOpen(!isDropdownOpen)} 
                            className={`relative cursor-pointer border dark:border-gray-600 rounded-md px-4 py-2 flex items-center justify-between ${
                                newStatus === 'pending' ? 'text-yellow-600 dark:text-yellow-400' : 
                                newStatus === 'rejected' ? 'text-red-600 dark:text-red-400' : 
                                newStatus === 'reject permanently' ? 'text-red-800 dark:text-red-500' : 
                                newStatus === 'redeemed' ? 'text-green-800 dark:text-green-400' : 
                                'text-gray-800 dark:text-gray-200'
                            } transition-colors duration-200`}
                        >
                            {newStatus}
                            <svg 
                                className={`transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} 
                                width="16" 
                                height="16" 
                                viewBox="0 0 16 16" 
                                fill="none" 
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path d="M8 12L2 6L3.4 4.6L8 9.2L12.6 4.6L14 6L8 12Z" fill="currentColor"/>
                            </svg>
                            
                            {isDropdownOpen && (
                                <div className="absolute top-full left-0 w-full mt-1 bg-white dark:bg-gray-700 border dark:border-gray-600 rounded-md shadow-lg z-10 transition-colors duration-200">
                                    <div className="py-2 px-4 hover:bg-gray-100 dark:hover:bg-gray-600 text-yellow-600 dark:text-yellow-400 cursor-pointer transition-colors duration-200" onClick={() => setNewStatus('pending')}>
                                        pending
                                    </div>
                                    <div className="py-2 px-4 hover:bg-gray-100 dark:hover:bg-gray-600 text-red-600 dark:text-red-400 cursor-pointer transition-colors duration-200" onClick={() => setNewStatus('rejected')}>
                                        rejected
                                    </div>
                                    <div className="py-2 px-4 hover:bg-gray-100 dark:hover:bg-gray-600 text-red-800 dark:text-red-500 cursor-pointer transition-colors duration-200" onClick={() => setNewStatus('reject permanently')}>
                                        reject permanently
                                    </div>
                                    <div className="py-2 px-4 hover:bg-gray-100 dark:hover:bg-gray-600 text-green-800 dark:text-green-400 cursor-pointer transition-colors duration-200" onClick={() => setNewStatus('redeemed')}>
                                        redeemed
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    
                    {(newStatus === 'rejected' || newStatus === 'reject permanently') && (
                        <div className="mt-3">
                            <p className="font-semibold text-gray-700 dark:text-gray-300 mb-2">Reason</p>
                            <input 
                                type="text" 
                                value={reason} 
                                onChange={(e) => setReason(e.target.value)} 
                                className="w-full px-4 py-2 border dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-200" 
                            />
                        </div>
                    )}
                </div>
                
                {/* Three-column layout for the rest of the data */}
                <div className="max-h-[60vh] overflow-y-auto space-y-4">
                    {/* User Information Section */}
                    <div className="border dark:border-gray-600 rounded-lg overflow-hidden">
                        <div 
                            className="bg-gray-100 dark:bg-gray-700 p-3 flex justify-between items-center cursor-pointer"
                            onClick={() => toggleSection('userInfo')}
                        >
                            <h3 className="font-semibold text-gray-800 dark:text-gray-200">User Information</h3>
                            {sectionsState.userInfo ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                        </div>
                        
                        {sectionsState.userInfo && (
                            <div className="p-4 grid grid-cols-2 gap-x-6 gap-y-3">
                                <div>
                                    <p className="font-semibold text-gray-700 dark:text-gray-300">ID: <span className="font-normal">{userData.id}</span></p>
                                </div>
                                <div>
                                    <p className="font-semibold text-gray-700 dark:text-gray-300">Name: <span className="font-normal">{userData.full_name}</span></p>
                                </div>
                                <div>
                                    <p className="font-semibold text-gray-700 dark:text-gray-300">Contact: <span className="font-normal">{userData.contact_no}</span></p>
                                </div>
                                <div>
                                    <p className="font-semibold text-gray-700 dark:text-gray-300">Email: <span className="font-normal">{userData.email}</span></p>
                                </div>
                            </div>
                        )}
                    </div>
                    
                    {/* Product Information Section */}
                    <div className="border dark:border-gray-600 rounded-lg overflow-hidden">
                        <div 
                            className="bg-gray-100 dark:bg-gray-700 p-3 flex justify-between items-center cursor-pointer"
                            onClick={() => toggleSection('productInfo')}
                        >
                            <h3 className="font-semibold text-gray-800 dark:text-gray-200">Product Information</h3>
                            {sectionsState.productInfo ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                        </div>
                        
                        {sectionsState.productInfo && (
                            <div className="p-4 grid grid-cols-2 gap-x-6 gap-y-3">
                                <div>
                                    <p className="font-semibold text-gray-700 dark:text-gray-300">Serial Number: <span className="font-normal">{userData.serial_number}</span></p>
                                </div>
                                <div>
                                    <p className="font-semibold text-gray-700 dark:text-gray-300">Category: <span className="font-normal">{userData.category}</span></p>
                                </div>
                                <div>
                                    <p className="font-semibold text-gray-700 dark:text-gray-300">Model: <span className="font-normal">{userData.model}</span></p>
                                </div>
                                <div>
                                    <p className="font-semibold text-gray-700 dark:text-gray-300">SKU: <span className="font-normal">{userData.sku}</span></p>
                                </div>
                            </div>
                        )}
                    </div>
                    
                    {/* Purchase Information Section */}
                    <div className="border dark:border-gray-600 rounded-lg overflow-hidden">
                        <div 
                            className="bg-gray-100 dark:bg-gray-700 p-3 flex justify-between items-center cursor-pointer"
                            onClick={() => toggleSection('purchaseInfo')}
                        >
                            <h3 className="font-semibold text-gray-800 dark:text-gray-200">Purchase Information</h3>
                            {sectionsState.purchaseInfo ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                        </div>
                        
                        {sectionsState.purchaseInfo && (
                            <div className="p-4 grid grid-cols-2 gap-x-6 gap-y-3">
                                <div>
                                    <p className="font-semibold text-gray-700 dark:text-gray-300">Invoice Number: <span className="font-normal">{userData.invoice_no}</span></p>
                                </div>
                                <div>
                                    <p className="font-semibold text-gray-700 dark:text-gray-300">Purchase Date: <span className="font-normal">{new Date(userData.purchased_date).toLocaleDateString('en-GB')}</span></p>
                                </div>
                                <div>
                                    <p className="font-semibold text-gray-700 dark:text-gray-300">Purchase Value: <span className="font-normal">RM {userData.purchased_value}</span></p>
                                </div>
                                <div>
                                    <p className="font-semibold text-gray-700 dark:text-gray-300">Usage: <span className="font-normal">{userData.home_office_usage}</span></p>
                                </div>
                                <div>
                                    <p className="font-semibold text-gray-700 dark:text-gray-300">Submission Date: <span className="font-normal">{new Date(userData.created_date_time).toLocaleString()}</span></p>
                                </div>
                                {userData.invoice_no_img && (
                                    <div>
                                        <p className="font-semibold text-gray-700 dark:text-gray-300">Invoice Image: 
                                            <button 
                                                type="button"
                                                onClick={() => {
                                                  if (userData.invoice_no_img.endsWith('.pdf')) {
                                                    viewPDF(userData.invoice_no_img);
                                                  } else {
                                                    setSelectedImage(userData.invoice_no_img);
                                                  }
                                                }}
                                                className="ml-2 text-blue-600 hover:text-blue-800 underline inline-flex items-center"
                                            >
                                                View Invoice <ExternalLink className="h-3 w-3 ml-1" />
                                            </button>
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
                
                <div className="flex justify-end space-x-3 mt-6">
                    <button 
                        onClick={onClose} 
                        className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
                    >
                        Cancel
                    </button>
                    <button 
                        onClick={handleSave} 
                        className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors duration-200"
                    >
                        Save
                    </button>
                </div>
            </div>
        </div>

        {/* Image Modal */}
        <Modal
          isOpen={!!selectedImage}
          onClose={() => setSelectedImage(null)}
        >
          <div className="flex items-center justify-center p-4">
            <img
              src={selectedImage || ''}
              alt="Invoice"
              className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-lg"
            />
          </div>
        </Modal>

        {/* PDF Modal */}
        <Modal
          isOpen={!!selectedPdf}
          onClose={() => {
            setSelectedPdf(null);
            setPdfLoadError(false);
          }}
        >
          <div className="flex flex-col items-center justify-center p-4 h-[80vh] w-full overflow-auto">
            {pdfLoadError ? (
              <div className="flex flex-col items-center justify-center space-y-4">
                <p className="text-red-500 dark:text-red-400">Failed to load PDF file due to security restrictions.</p>
                <div className="flex space-x-4">
                  <a 
                    href={selectedPdf || ''} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors duration-200"
                  >
                    Open in New Tab
                  </a>
                  <button
                    onClick={() => window.open(selectedPdf || '', '_blank')}
                    className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors duration-200"
                  >
                    Download PDF
                  </button>
                </div>
              </div>
            ) : (
              <>
                <Document
                  file={selectedPdf || ''}
                  onLoadError={onDocumentLoadError}
                  onLoadSuccess={onDocumentLoadSuccess}
                  className="w-full h-full rounded-lg shadow-lg"
                >
                  <Page 
                    pageNumber={pdfCurrentPage} 
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                    width={Math.min(window.innerWidth * 0.8, 800)}
                  />
                </Document>
                
                {pdfNumPages && pdfNumPages > 1 && (
                  <div className="flex items-center justify-center mt-4 space-x-4">
                    <button 
                      onClick={() => setPdfCurrentPage(page => Math.max(page - 1, 1))}
                      disabled={pdfCurrentPage <= 1}
                      className="px-3 py-1 bg-gray-200 dark:bg-gray-700 rounded-md disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <span className="text-sm">
                      Page {pdfCurrentPage} of {pdfNumPages}
                    </span>
                    <button 
                      onClick={() => setPdfCurrentPage(page => Math.min(page + 1, pdfNumPages || 1))}
                      disabled={pdfCurrentPage >= (pdfNumPages || 1)}
                      className="px-3 py-1 bg-gray-200 dark:bg-gray-700 rounded-md disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </Modal>
        </>
    );
}